import { totalCentsAsString } from 'store/modules/bookingBuilder/utils';
import { isNilOrEmpty } from 'ramda-adjunct';

import { TCurrencyCode } from 'interfaces';
import { parseISO, isBefore } from 'date-fns';
import { formatPriceCents, getCurrencySymbol } from 'utils';

interface IGetFromPricesReturn {
  fromPrice: string | null;
  fromPriceBeforeDiscount: string | null;
  appliedOfferNames: string[];
  isLiveRate: boolean;
}

interface iCancellationPolicyWithMeta {
  cancellationPolicy: string;
  meta: {
    isInPast: boolean;
  };
}
export const deadlinesToCancellationPolicies = (
  currencyCode: TCurrencyCode,
  expensesDeadlines:
    | {
        deadline: string;
        amount: number;
      }[]
    | null
    | undefined
) => {
  if (expensesDeadlines === null || expensesDeadlines === undefined) {
    return [];
  }

  let deadlines = expensesDeadlines
    .sort((a, b) => (a.deadline > b.deadline ? 1 : -1))
    .map(ed => {
      const dateObj = parseISO(ed.deadline);
      const [date, time] = ed.deadline.split(' ');
      const [year, month, day] = date.split('-');
      const [hour, minute, second] = time.split(':');
      const deadline = `${day}/${month}/${year} at ${hour}:${minute}`;
      return {
        ...ed,
        amount: `${getCurrencySymbol(currencyCode)}${formatPriceCents(ed.amount, 2)}`,
        deadline,
        dateObj,
      };
    });

  const cancellationPolicys: iCancellationPolicyWithMeta[] = [];
  const now = new Date();

  // build the first one
  if (deadlines.length >= 1) {
    cancellationPolicys.push({
      cancellationPolicy: `Cancel before ${deadlines[0].deadline}* Free`,
      meta: {
        isInPast: isBefore(deadlines[0].dateObj, now),
      },
    });
  }

  // then loop over the others, if we have enough to be worth looping over
  if (deadlines.length >= 2) {
    deadlines.forEach((d, dIdx) => {
      if (deadlines[dIdx + 1] != null) {
        cancellationPolicys.push({
          cancellationPolicy: `Cancel between ${d.deadline}* - ${deadlines[dIdx + 1].deadline}* ${d.amount}`,
          meta: {
            isInPast: isBefore(deadlines[dIdx + 1].dateObj, now),
          },
        });
      }
    });
  }

  // then add 1 final one for the last index item
  if (deadlines.length >= 1) {
    const lastIndex = deadlines.length - 1;
    cancellationPolicys.push({
      cancellationPolicy: `Cancel after ${deadlines[lastIndex].deadline}* ${deadlines[lastIndex].amount}`,
      meta: {
        isInPast: false, // this cancellation policy is always "Cancel *AFTER*" so it's never in the past
      },
    });
  }

  return cancellationPolicys;
};

const getTotalCents = (rate: { totalCents: number }): number => rate.totalCents;

export const getFromPrices = (staticRates, liveRates): IGetFromPricesReturn => {
  interface ICandidate {
    price: number;
    fromPrice: string;
    fromPriceBeforeDiscount: string;
    appliedOfferNames: string[];
    isLiveRate: boolean;
  }

  const candidates: ICandidate[] = [];

  // collect all priced static rates (any occupancy type)
  for (const sr of staticRates) {
    if (sr.totals && !sr.totals.oneOrMoreItemsOnRequest) {
      candidates.push({
        price: parseFloat(sr.totals.total),
        fromPrice: sr.totals.total,
        fromPriceBeforeDiscount: sr.totals.totalBeforeDiscount,
        appliedOfferNames: sr.appliedOfferNames || [],
        isLiveRate: false,
      });
    }
  }

  // collect all priced live rates (find cheapest entry within each rate group)
  if (Array.isArray(liveRates)) {
    for (const lr of liveRates) {
      if (!isNilOrEmpty(lr.list)) {
        const minEntry = lr.list.reduce((min, current) =>
          getTotalCents(current) < getTotalCents(min) ? current : min, lr.list[0]
        );
        const dollarString = totalCentsAsString(minEntry.totalCents);
        candidates.push({
          price: parseFloat(dollarString),
          fromPrice: dollarString,
          fromPriceBeforeDiscount: dollarString,
          appliedOfferNames: [],
          isLiveRate: true,
        });
      }
    }
  }

  if (candidates.length === 0) {
    return { fromPrice: null, fromPriceBeforeDiscount: null, appliedOfferNames: [], isLiveRate: false };
  }

  const cheapest = candidates.reduce((min, c) => (c.price < min.price ? c : min));


  return {
    fromPrice: cheapest.fromPrice,
    fromPriceBeforeDiscount: cheapest.fromPriceBeforeDiscount,
    appliedOfferNames: cheapest.appliedOfferNames,
    isLiveRate: cheapest.isLiveRate,
  };
};
